/* ==========================================================================
   Lumira Animations Loader
   - Auto-tags Lumira + Ellara sections with data-lum-anim attributes using a
     HIERARCHICAL vocabulary (headings rise, eyebrows fade, body fade-up,
     stats/badges/CTAs scale-in, comparison rows slide, media clip-reveal).
   - Tag-based heading/body selectors ensure ALL text is covered.
   - IntersectionObserver adds .is-revealed when in view.
   - Respects prefers-reduced-motion (reveals everything immediately).
   - Idempotent + reruns on Shopify section load events.
   ========================================================================== */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const LUMIRA_SECTION_SELECTOR = [
    '.lumira-section',
    '.lumira-hero',
    '.lumira-footer',
    '.lumira-header',
    '.ellara-section',
    '.ellara-exit-section',
    '.shopify-section[id*="home-"]',
    '.shopify-section[id*="home_"]',
    '.shopify-section[id*="prelaunch-"]',
    '.shopify-section[id*="support-"]',
    '.shopify-section[id*="lumira-"]'
  ].join(',');

  // Element rules, evaluated in order; first rule to claim an element wins.
  const RULES = [
    // Eyebrows / small labels -> quiet fade
    { sel: '.lh-eyebrow,.lp-eyebrow,.ld-eyebrow,.lr-eyebrow,.lc-eyebrow,.lcb-eyebrow,.lf-eyebrow,.lrv-eyebrow,.lo-eyebrow,.lm-eyebrow,.lib-label,.sci__eyebrow,.sol__eyebrow,.lhh2-eyebrow,.ellara-pain__eyebrow,.ellara-faq__eyebrow', anim: 'fade' },

    // Stat numbers / figures -> scale-in (before headings so they are not claimed as 'rise')
    { sel: '.sol__num,.lh-step-num,.lo-step-num,.lib-num,.sci__stat,.sci__figure,.ellara-pain__num', anim: 'scale-in' },

    // Price / save badges -> scale-in
    { sel: '.lh-save,.lo-save,.lh-price,.lo-price', anim: 'scale-in' },

    // Headings -> premium rise (lift + blur clear). Tag-based covers ALL headings.
    { sel: 'h1,h2,h3,h4,.lh-h2,.lp-h2,.ld-h2,.lr-h2,.lc-h2,.lcb-h2,.lf-h2,.lrv-h2,.lo-h2,.sci__h2,.sol__h2,.lh-name,.lr-name,.lh-step-title,.lo-step-title', anim: 'rise' },

    // Buttons / CTAs -> scale-in
    { sel: '.lh-cta,.lo-button,.lhd-cta,.button,.btn,.btn--primary', anim: 'scale-in' },

    // Comparison rows -> slide-right (before generic body/li)
    { sel: '.lcb-row,.lc-row', anim: 'slide-right' },

    // Cards / steps / items -> fade-up (stagger adds rhythm)
    { sel: '.lh-bullet,.lh-step,.lo-step,.lr-card,.lrv-card,.lf-col,.lib-cell,.lp-card,.ld-card,.lf-faq,.ellara-pain__item', anim: 'fade-up' },

    // Images / media -> cinematic clip-reveal (section direction may override)
    { sel: '.lh-img,.lh-media,.ld-img,.lr-img,.lo-img,.lp-img,.lib-img', anim: 'clip-reveal' },

    // Generic media
    { sel: 'picture,video,.media,.image-block', anim: 'clip-reveal' },

    // Body copy -> fade-up. Tag-based catch-all covers ALL remaining text.
    { sel: 'p,li,blockquote,.lh-tagline,.lh-step-body,.lr-quote,.lrv-quote,.lp-body,.lo-sub,.lib-desc,.sci__lead,.sol__lead', anim: 'fade-up' }
  ];

  const IMG_DIRECTION_BY_SECTION = [
    { test: /lumira-hero|home-01|home_01|prelaunch-02/, anim: 'slide-left' },
    { test: /lumira-problem-serums/,            anim: 'slide-right' },
    { test: /lumira-dermatologist/,             anim: 'slide-left' },
    { test: /lumira-before-after|home-05/,      anim: 'zoom-in' },
    { test: /lumira-how-it-works/,              anim: 'fade-up' },
    { test: /lumira-reviews|home-07/,           anim: 'fade-up' }
  ];

  function pickImageAnim(sectionEl) {
    const id = (sectionEl.id || '') + ' ' + (sectionEl.className || '');
    for (const r of IMG_DIRECTION_BY_SECTION) {
      if (r.test.test(id)) return r.anim;
    }
    return 'clip-reveal';
  }

  function decorateSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.lumDecorated === '1') return;
    sectionEl.dataset.lumDecorated = '1';

    const imageAnim = pickImageAnim(sectionEl);

    RULES.forEach(rule => {
      const nodes = sectionEl.querySelectorAll(rule.sel);
      let counter = 0;
      nodes.forEach(node => {
        if (node.hasAttribute('data-lum-anim')) return;
        if (node.closest('[data-lum-skip]')) return;

        let anim = rule.anim;
        if (rule.sel.includes('lh-img') || rule.sel.includes('lh-media') ||
            rule.sel.includes('ld-img') || rule.sel.includes('lr-img') ||
            rule.sel.includes('lo-img') || rule.sel.includes('lp-img') ||
            rule.sel.includes('lib-img')) {
          anim = imageAnim;
        }

        node.setAttribute('data-lum-anim', anim);

        if (counter > 0 && counter <= 8) {
          node.setAttribute('data-lum-delay', String(counter));
        }
        counter++;
      });
    });

    const STAGGER_GROUPS = '.lh-bullets,.lh-steps,.lo-steps,.lib-grid,.lr-grid,.lrv-grid,.lf-cols,.lcb-table,.lc-table,.lp-grid,.ld-grid,.ellara-pain__list';
    sectionEl.querySelectorAll(STAGGER_GROUPS).forEach(group => {
      const children = group.children;
      let i = 0;
      for (const child of children) {
        if (!child.hasAttribute('data-lum-anim')) {
          child.setAttribute('data-lum-anim', 'fade-up');
        }
        child.setAttribute('data-lum-delay', String(Math.min(i, 8)));
        i++;
      }
    });
  }

  function getSections(root) {
    return (root || document).querySelectorAll(LUMIRA_SECTION_SELECTOR);
  }

  let observer;
  function ensureObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.05
    });
    return observer;
  }

  function observeAll(root) {
    const obs = ensureObserver();
    const targets = (root || document).querySelectorAll('[data-lum-anim]:not(.is-revealed)');
    targets.forEach(t => obs.observe(t));
  }

  function revealAllImmediately(root) {
    const targets = (root || document).querySelectorAll('[data-lum-anim]:not(.is-revealed)');
    targets.forEach(t => t.classList.add('is-revealed'));
  }

  function run(root) {
    if (REDUCED) {
      const sections = getSections(root);
      sections.forEach(decorateSection);
      revealAllImmediately(root);
      return;
    }
    const sections = getSections(root);
    sections.forEach(decorateSection);
    observeAll(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run());
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', (e) => run(e.target));
  document.addEventListener('shopify:section:select', (e) => run(e.target));
  document.addEventListener('shopify:block:load', (e) => run(e.target));

  window.addEventListener('pageshow', () => run());

  window.LumiraAnim = { run, revealAll: revealAllImmediately, _RULES: RULES };
})();


/* ==========================================================================
   Ellara Motion+ (v1) — appended interactivity layer
   Adds what the base reveal system doesn't: tasteful hover "pops", hero gallery
   zoom, LED-dot ambient glow, number count-ups and cost-bar fills.
   Self-contained, try/catch-wrapped IIFE: it can never affect the reveal code
   above it. Numbers/bars show their final value by default and only animate
   when scrolled into view, so nothing is ever hidden if JS/observer fails.
   ========================================================================== */
(function () {
  'use strict';
  try {
    if (typeof document === 'undefined') return;
    var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---- 1) Inject motion styles (hover, glow, gallery, CTA) ---------------- */
    var CSS = [
      ':root{--em-ease:cubic-bezier(.16,.84,.32,1);--em-spring:cubic-bezier(.34,1.56,.64,1);}',
      '.lib-cell,.sci__spec,.sci__wave,.sol__item,.lr-card,.lh-step,.lm-cell,.ellara-hero .lh-trust{transition:transform .35s var(--em-ease),box-shadow .35s var(--em-ease),border-color .35s var(--em-ease),background-color .35s var(--em-ease);}',
      '.lm-icon,.lib-icon,.lh-step-icon,.ellara-hero .lh-trust svg{transition:transform .4s var(--em-spring);}',
      '.lm-cell:hover{border-color:rgba(201,169,110,.55) !important;}',
      '.lf-item{transition:background-color .35s var(--em-ease);}',
      '.lf-item:hover{background-color:rgba(201,169,110,.05);}',
      '.lcb-bar-fill{transition:filter .35s var(--em-ease);}',
      '.lcb-row:hover .lcb-bar-fill{filter:brightness(1.06) saturate(1.05);}',
      '.ellara-hero .lh-main{transition:box-shadow .5s var(--em-ease);}',
      '.ellara-hero .lh-main-img{transition:transform .6s var(--em-ease);}',
      '.ellara-hero .lh-thumb{transition:border-color .2s ease,transform .28s var(--em-ease),box-shadow .28s var(--em-ease);}',
      '.ellara-hero .lh-thumb img{transition:transform .45s var(--em-ease);}',
      '.ellara-hero .lh-cta,.lo-button,.ellara-stickybar .lsb-cta{transition:transform .25s var(--em-ease),box-shadow .3s var(--em-ease),background-color .25s var(--em-ease),color .25s var(--em-ease);}',
      '@keyframes emDotGlow{0%,100%{box-shadow:0 0 0 1px rgba(255,255,255,.45) inset,0 4px 14px -4px rgba(44,26,14,.30);}50%{box-shadow:0 0 0 1px rgba(255,255,255,.6) inset,0 6px 22px -2px rgba(201,169,110,.6);}}',
      '@keyframes emSheen{0%{transform:translateX(-130%);}28%,100%{transform:translateX(230%);}}',
      '@media (prefers-reduced-motion: no-preference){',
        '.lib-cell:hover,.sol__item:hover,.lr-card:hover,.lh-step:hover,.lm-cell:hover{transform:translateY(-5px);box-shadow:0 22px 44px -26px rgba(44,26,14,.40);}',
        '.sci__spec:hover{transform:translateY(-3px);}',
        '.ellara-hero .lh-trust:hover{transform:translateY(-3px);}',
        '.lm-cell:hover .lm-icon,.lib-cell:hover .lib-icon,.lh-step:hover .lh-step-icon,.ellara-hero .lh-trust:hover svg{transform:scale(1.12);}',
        '.sci__dot{animation:emDotGlow 3.6s var(--em-ease) infinite;transition:transform .4s var(--em-spring);}',
        '.sci__wave:nth-child(2) .sci__dot{animation-delay:.45s;}',
        '.sci__wave:nth-child(3) .sci__dot{animation-delay:.9s;}',
        '.sci__wave:nth-child(4) .sci__dot{animation-delay:1.35s;}',
        '.sci__wave:nth-child(5) .sci__dot{animation-delay:1.8s;}',
        '.sci__wave:nth-child(6) .sci__dot{animation-delay:2.25s;}',
        '.sci__wave:hover .sci__dot{transform:scale(1.18);}',
        '.ellara-hero .lh-main:hover{box-shadow:0 34px 64px -34px rgba(44,26,14,.38);}',
        '.ellara-hero .lh-main:hover .lh-main-img.is-active{transform:scale(1.06);}',
        '.ellara-hero .lh-thumb:hover{transform:translateY(-2px);box-shadow:0 8px 18px -10px rgba(44,26,14,.45);}',
        '.ellara-hero .lh-thumb:hover img{transform:scale(1.06);}',
        '.ellara-hero .lh-badge{position:absolute;overflow:hidden;}',
        '.ellara-hero .lh-badge::after{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(120deg,transparent 32%,rgba(255,255,255,.28) 50%,transparent 68%);transform:translateX(-130%);animation:emSheen 5s var(--em-ease) 1.6s infinite;}',
        '.ellara-hero .lh-cta:hover,.lo-button:hover{transform:translateY(-2px);box-shadow:0 14px 30px -12px rgba(44,26,14,.5);}',
      '}'
    ].join('');
    var st = document.createElement('style');
    st.id = 'ellara-motion-styles';
    st.textContent = CSS;
    (document.head || document.documentElement).appendChild(st);

    /* ---- 2) Count-ups + cost-bar fills (animate on scroll-in only) ---------- */
    function init() {
      var main = document.getElementById('MainContent') || document.body;
      if (!main) return;

      function firstNumNode(el) {
        for (var i = 0; i < el.childNodes.length; i++) {
          var n = el.childNodes[i];
          if (n.nodeType === 3 && /\d/.test(n.nodeValue)) return n;
        }
        return null;
      }
      function parseNum(s) {
        var m = String(s).trim().match(/^([^\d-]*)(\d[\d,]*)([^\d]*)$/);
        if (!m) return null;
        var t = parseInt(m[2].replace(/,/g, ''), 10);
        if (isNaN(t)) return null;
        return { prefix: m[1], suffix: m[3], target: t, commas: (/,/.test(m[2]) || t >= 1000) };
      }
      function fmt(n, c) { return c ? n.toLocaleString('en-US') : String(n); }
      function countNode(node) {
        if (!node) return;
        var info = parseNum(node.nodeValue);
        if (!info || REDUCE) return;
        var dur = 1100, start = null;
        function step(ts) {
          if (start === null) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var e = 1 - Math.pow(1 - p, 3);
          node.nodeValue = info.prefix + fmt(Math.round(info.target * e), info.commas) + info.suffix;
          if (p < 1) requestAnimationFrame(step);
          else node.nodeValue = info.prefix + fmt(info.target, info.commas) + info.suffix;
        }
        requestAnimationFrame(step);
      }

      var counters = [];
      function addCounter(numHost, ioHost) {
        counters.push({ host: ioHost, done: false, run: function () { countNode(firstNumNode(numHost)); } });
      }
      main.querySelectorAll('.sci__num').forEach(function (el) { addCounter(el, el.closest('.sci__specs') || el); });
      main.querySelectorAll('.lcb-amount').forEach(function (el) { addCounter(el.querySelector('em') || el, el.closest('.lcb-chart') || el); });
      main.querySelectorAll('.lcb-savings-num').forEach(function (el) { addCounter(el, el.closest('.lcb-chart') || el); });

      var bars = [];
      main.querySelectorAll('.lcb-bar-fill').forEach(function (el) {
        var target = el.style.width || '';
        if (!target) return;
        bars.push({ el: el, host: el.closest('.lcb-chart') || el, target: target, done: false });
      });
      function fillBar(b) {
        if (b.done) return;
        b.done = true;
        if (REDUCE) return;
        var el = b.el, t = b.target;
        el.style.transition = 'none';
        el.style.width = '0%';
        void el.offsetWidth;
        el.style.transition = 'width 1.15s cubic-bezier(.16,.84,.32,1)';
        el.style.width = t;
      }

      function fire(container) {
        counters.forEach(function (c) { if (!c.done && container.contains(c.host)) { c.done = true; c.run(); } });
        bars.forEach(function (b) { if (!b.done && container.contains(b.host)) { fillBar(b); } });
      }

      var hosts = [];
      main.querySelectorAll('.sci__specs, .lcb-chart').forEach(function (h) { hosts.push(h); });

      if (REDUCE || !('IntersectionObserver' in window)) {
        hosts.forEach(fire);
      } else {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) { fire(e.target); io.unobserve(e.target); } });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.2 });
        hosts.forEach(function (h) { io.observe(h); });
        setTimeout(function () { hosts.forEach(fire); }, 4000);
      }

      setTimeout(function () {
        document.querySelectorAll('[data-lum-anim]:not(.is-revealed)').forEach(function (t) {
          var r = t.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) t.classList.add('is-revealed');
        });
      }, 4500);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
  } catch (e) { /* never break the page */ }
})();
